import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { companiesQueryOptions } from '@/entities/company'
import { employeesQueryOptions } from '@/entities/employee'
import { priorityTone, STAGE_DEADLINE_PRESETS } from '@/entities/project'
import { tagSuggestionsQueryOptions } from '@/entities/tag'
import { APP_TZ, cn, flattenPages, zoneTag } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/field'
import { Segmented } from '@/shared/ui/segmented'
import { zoneColumnLabel } from '@/shared/ui/zone-badge'
import {
  type BoardFilterValue,
  DEADLINE_PRESET_LABELS,
  EMPTY_BOARD_FILTER,
  rangeInvalid,
} from '../model/filter'

interface BoardFilterDialogProps {
  open: boolean
  onClose: () => void
  value: BoardFilterValue
  onApply: (value: BoardFilterValue) => void
}

// Модалка фильтра доски: наборы (ответственные / приоритеты / теги / компании),
// срок шага и переключатели скрытых-архивных. Правки копятся в черновике и
// уезжают в адрес одним «Применить» — промежуточные состояния истории не пишем.
export function BoardFilterDialog({ open, onClose, value, onApply }: BoardFilterDialogProps) {
  // Черновик заводится от адреса на монтировании: страница держит диалог
  // условно, каждое открытие — свежий инстанс с актуальным фильтром.
  const [draft, setDraft] = useState<BoardFilterValue>(value)

  const employeesQ = useQuery(employeesQueryOptions)
  const companiesQ = useInfiniteQuery(companiesQueryOptions({ includeArchived: true }))
  const tagsQ = useQuery(tagSuggestionsQueryOptions(''))

  const companies = flattenPages(companiesQ.data?.pages)
  // Выбранный тег мог прийти deep-link'ом и не попасть в топ словаря — держим его
  // в списке, иначе снять выбор было бы нечем.
  const tagNames = [...new Set([...draft.tags, ...(tagsQ.data ?? []).map((t) => t.name)])]
  const invalidRange = rangeInvalid(draft)

  const patch = (next: Partial<BoardFilterValue>) => setDraft({ ...draft, ...next })
  const toggle = <T extends string | number>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item]

  // Режим срока: пресет или произвольный диапазон. Переключение чистит второй
  // режим — отправить оба разом (400 у бэка) нельзя даже случайно.
  const deadlineMode: 'preset' | 'range' =
    draft.from != null || draft.to != null ? 'range' : 'preset'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Фильтр доски"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => setDraft(EMPTY_BOARD_FILTER)}>
            Сбросить
          </Button>
          <Button size="sm" disabled={invalidRange} onClick={() => onApply(draft)}>
            Применить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Section label="Ответственные">
          {employeesQ.data?.length ? (
            <ChipCloud>
              {employeesQ.data.map((e) => (
                <FilterChip
                  key={e.id}
                  active={draft.responsible.includes(e.id)}
                  onClick={() => patch({ responsible: toggle(draft.responsible, e.id) })}
                >
                  {e.full_name}
                </FilterChip>
              ))}
            </ChipCloud>
          ) : (
            <Muted>Сотрудников нет</Muted>
          )}
        </Section>

        <Section label="Приоритеты">
          <ChipCloud>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
              <FilterChip
                key={p}
                active={draft.priorities.includes(p)}
                onClick={() => patch({ priorities: toggle(draft.priorities, p) })}
                className={cn('font-mono', !draft.priorities.includes(p) && priorityTone(p))}
              >
                P{p}
              </FilterChip>
            ))}
          </ChipCloud>
        </Section>

        {/* Окна «Просрочено / Сегодня / 7 / 30 дней» отсчитываются от суток
            зоны организации — секция подписана ею целиком. */}
        <Section label={zoneColumnLabel('Срок шага', APP_TZ)}>
          <div className="flex flex-col gap-2">
            <Segmented
              label="Режим срока"
              options={[
                { value: 'preset', label: 'Окно' },
                { value: 'range', label: 'Диапазон' },
              ]}
              value={deadlineMode}
              onChange={(mode) =>
                setDraft({
                  ...draft,
                  deadline: undefined,
                  from: undefined,
                  // Диапазон открывается пустым; окно — без пресета, его выбирают чипом.
                  to: undefined,
                  ...(mode === 'range' ? { from: '', to: '' } : {}),
                })
              }
            />
            {deadlineMode === 'preset' ? (
              <ChipCloud>
                {STAGE_DEADLINE_PRESETS.map((preset) => (
                  <FilterChip
                    key={preset}
                    active={draft.deadline === preset}
                    onClick={() =>
                      patch({ deadline: draft.deadline === preset ? undefined : preset })
                    }
                  >
                    {DEADLINE_PRESET_LABELS[preset]}
                  </FilterChip>
                ))}
              </ChipCloud>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="date"
                    aria-label="Срок с"
                    value={draft.from ?? ''}
                    max={draft.to || undefined}
                    onChange={(e) => patch({ from: e.target.value })}
                    className="w-44 px-3 py-2 text-sm"
                  />
                  <span className="text-text-tertiary text-sm">—</span>
                  <Input
                    type="date"
                    aria-label="Срок по"
                    value={draft.to ?? ''}
                    min={draft.from || undefined}
                    onChange={(e) => patch({ to: e.target.value })}
                    className="w-44 px-3 py-2 text-sm"
                  />
                </div>
                {/* Границы — календарные дни, но окно закрывает МОМЕНТЫ срока
                    шага: сутки режутся по зоне организации, и об этом надо
                    сказать — иначе «по 31.07» читается как «до полуночи у меня». */}
                <span className="text-text-tertiary text-xs">
                  Дни включительно; границы суток — в зоне организации (
                  {zoneTag(new Date(), APP_TZ)})
                </span>
                {invalidRange && (
                  <span className="text-accent text-xs">Начало диапазона позже конца</span>
                )}
              </div>
            )}
          </div>
        </Section>

        <Section label="Теги">
          {tagNames.length > 0 ? (
            <ChipCloud>
              {tagNames.map((name) => (
                <FilterChip
                  key={name}
                  active={draft.tags.includes(name)}
                  onClick={() => patch({ tags: toggle(draft.tags, name) })}
                >
                  {name}
                </FilterChip>
              ))}
            </ChipCloud>
          ) : (
            <Muted>Тегов пока нет</Muted>
          )}
        </Section>

        <Section label="Компании">
          {companies.length > 0 ? (
            <ChipCloud scroll>
              {companies.map((c) => (
                <FilterChip
                  key={c.id}
                  active={draft.companies.includes(c.id)}
                  onClick={() => patch({ companies: toggle(draft.companies, c.id) })}
                >
                  {c.name}
                </FilterChip>
              ))}
            </ChipCloud>
          ) : (
            <Muted>Компаний нет</Muted>
          )}
        </Section>

        <Section label="Показывать">
          <div className="flex flex-wrap items-center gap-4">
            <Checkbox
              label="Скрытые в deal"
              labelClassName="text-sm text-text-secondary"
              checked={draft.hidden}
              onChange={(e) => patch({ hidden: e.target.checked })}
            />
            <Checkbox
              label="Архивные"
              labelClassName="text-sm text-text-secondary"
              checked={draft.archived}
              onChange={(e) => patch({ archived: e.target.checked })}
            />
          </div>
        </Section>
      </div>
    </Dialog>
  )
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="label">{label}</h3>
      {children}
    </section>
  )
}

function ChipCloud({ children, scroll = false }: { children: ReactNode; scroll?: boolean }) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', scroll && 'max-h-40 overflow-y-auto pr-1')}>
      {children}
    </div>
  )
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-sm text-text-tertiary">{children}</span>
}

// Чип-переключатель значения набора: нажатое состояние — aria-pressed, чтобы
// выбор читался скринридером, а не только цветом.
function FilterChip({
  active,
  onClick,
  className,
  children,
}: {
  active: boolean
  onClick: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'max-w-full truncate rounded-pill px-2.5 py-1 text-sm ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
        active
          ? 'bg-accent text-text-inverse ring-accent'
          : 'bg-bg-surface text-text-secondary ring-border-subtle hover:text-text-primary',
        className,
      )}
    >
      {children}
    </button>
  )
}
